import { useAuth } from "contexts/AuthContext";
import {
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  FileStack,
  Landmark,
  RefreshCw,
  UserRound,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  DateRange,
  loadModuleCount,
  ModuleCount,
  ModuleDef,
  ModuleKey,
  MODULES,
  visibleModules,
} from "./dashboardLists";
import { CountBars, CountUp, Donut, Slice } from "./DashboardCharts";
import EvaluationSummary from "./EvaluationSummary";

type ModuleState =
  | { state: "loading" }
  | { state: "ok"; data: ModuleCount }
  | { state: "error" };

// Roles that evaluate prosecutor teams and see their evaluation summary.
const EVALUATOR_ROLES = [
  "LRDIFUDDirector",
  "LegalResearchCoordinator",
  "LegalTranslationCordinator",
  "ImpAwarenessCordinator",
];

// Time filter. Relative periods end now; a year is 1 January to 31 December.
const PERIODS = [
  { key: "all", labelKey: "rd_period_all" },
  { key: "today", labelKey: "rd_today" },
  { key: "week", labelKey: "rd_this_week" },
  { key: "month", labelKey: "rd_this_month" },
  { key: "3m", labelKey: "rd_last_3_months" },
  { key: "6m", labelKey: "rd_last_6_months" },
  { key: "12m", labelKey: "rd_last_12_months" },
];
const YEARS_BACK = 5;

const periodRange = (period: string): DateRange | undefined => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const monthsAgo = (n: number) => new Date(now.getFullYear(), now.getMonth() - n, now.getDate());
  if (period.startsWith("year:")) {
    const y = Number(period.slice(5));
    return { from: new Date(y, 0, 1), to: new Date(y + 1, 0, 1) };
  }
  switch (period) {
    case "today":
      return { from: today, to: tomorrow };
    case "week": {
      // The week starts on Monday.
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      return { from: monday, to: tomorrow };
    }
    case "month":
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: tomorrow };
    case "3m":
      return { from: monthsAgo(3), to: tomorrow };
    case "6m":
      return { from: monthsAgo(6), to: tomorrow };
    case "12m":
      return { from: monthsAgo(12), to: tomorrow };
    default:
      return undefined;
  }
};

// The same colours as the prosecution dashboard.
const PALETTE = [
  "#AA232F",
  "#DC2626",
  "#F59E0B",
  "#9D0303",
  "#212121",
  "#16A34A",
  "#2563EB",
  "#7F1D1D",
  "#D97706",
  "#414040",
  "#0A1D39",
  "#747171",
];
const TOP_CARDS = 5;
// A list that has not answered by then shows "—" instead of loading forever.
const MODULE_TIMEOUT_MS = 60000;

const withTimeout = <T,>(promise: Promise<T>) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), MODULE_TIMEOUT_MS);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

// Status tabs that are the user's own queue. Research statuses do not name
// who acts next, so this goes by the kind of status: coordinators, directors
// and heads act on new incoming items, prosecutors on work in progress.
const normalizeStatus = (key: string) => key.toLowerCase().replace(/[\s-]+/g, "_");
const actionMatchers = (roles: string[] = [], external = false) => {
  const text = roles.join(" ").toLowerCase();
  const matchers: ((status: string) => boolean)[] = [];
  // External users (stakeholders) act when the office asks them for
  // something or sends them a paper to review.
  if (external) {
    matchers.push(
      (k) =>
        k.includes("additional_info") ||
        k.includes("additional_information") ||
        k.includes("title_requested") ||
        k.includes("proposal_requested") ||
        k.includes("sent_to_stakeholder") ||
        k.includes("request_responded") ||
        k.includes("public_feedback"),
    );
    return (status: string) => matchers.some((match) => match(normalizeStatus(status)));
  }
  if (/coordinator|cordinator|director|head/.test(text)) {
    matchers.push(
      (k) =>
        k.includes("submitted") ||
        k.includes("received") ||
        k === "pending" ||
        k.includes("requested"),
    );
  }
  if (/prosecutor/.test(text)) {
    matchers.push(
      (k) =>
        k.includes("under_analysis") ||
        k.includes("under_review") ||
        k.includes("revision") ||
        k.includes("team_appointed") ||
        k.includes("responded"),
    );
  }
  return (status: string) => matchers.some((match) => match(normalizeStatus(status)));
};

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const Card: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, action, children, className = "" }) => (
  <section className={`flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow ${className}`}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {action}
    </div>
    <div className="flex-1">{children}</div>
  </section>
);

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { user } = auth;
  const bearer = auth.token || user?.access_token || "";
  const isEvaluator = !!user?.roles?.some((r: string) => EVALUATOR_ROLES.includes(r));

  const [modules, setModules] = useState<ModuleKey[]>([]);
  const [results, setResults] = useState<Partial<Record<ModuleKey, ModuleState>>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("all");
  const range = useMemo(() => periodRange(period), [period]);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: YEARS_BACK + 1 }, (_, i) => currentYear - i);

  const defOf = (key: ModuleKey): ModuleDef | undefined => MODULES.find((m) => m.key === key);
  const moduleLabel = (key: ModuleKey) => {
    const def = defOf(key);
    if (!def) return key;
    const own = t(def.labelKey, { defaultValue: def.label ?? def.labelKey });
    return def.parentLabelKey ? `${t(def.parentLabelKey)} · ${own}` : own;
  };
  const colorOf = (key: ModuleKey) => {
    const index = MODULES.findIndex((m) => m.key === key);
    return PALETTE[(index < 0 ? 0 : index) % PALETTE.length];
  };

  const load = useCallback(() => {
    if (!user) return;
    const keys = visibleModules(user);
    setModules(keys);
    setResults(Object.fromEntries(keys.map((k) => [k, { state: "loading" }])));
    setRefreshing(true);
    Promise.all(
      keys.map((key) =>
        withTimeout(loadModuleCount(key, user, range))
          .then((data) => setResults((r) => ({ ...r, [key]: { state: "ok", data } })))
          .catch(() => setResults((r) => ({ ...r, [key]: { state: "error" } }))),
      ),
    ).finally(() => setRefreshing(false));
  }, [user, range]);

  useEffect(() => {
    load();
  }, [load]);

  const dataOf = (key: ModuleKey) => {
    const result = results[key];
    return result?.state === "ok" ? result.data : undefined;
  };
  const statusLabel = (s: { label: string; labelKey?: string }) =>
    t(s.labelKey ?? s.label, { defaultValue: s.label });
  const linkOf = (key: ModuleKey) => {
    const def = defOf(key);
    return { to: def?.route ?? "/", state: def?.state };
  };

  const topCards = modules.slice(0, TOP_CARDS);
  const chips = modules.slice(TOP_CARDS);
  const main = modules[0];
  const withStatus = modules.filter((k) => (dataOf(k)?.byStatus.length ?? 0) > 0);
  const [statusFirst, statusSecond] = withStatus;
  const withRecent = modules.filter((k) => (dataOf(k)?.recent.length ?? 0) > 0);

  const isWaiting = actionMatchers(user?.roles, user?.user_type === "external");
  const waiting = modules
    .flatMap((key) =>
      (dataOf(key)?.byStatus ?? [])
        .filter((s) => s.count > 0 && isWaiting(s.key))
        .map((s) => ({ key, status: s })),
    )
    .sort((a, b) => b.status.count - a.status.count);
  const stillLoading = modules.some((k) => results[k]?.state === "loading");

  const displayName =
    user?.full_name ||
    (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : "");
  const roleName =
    (user?.roles?.length ?? 0) > 1 ? user?.roles?.[1] : user?.roles?.[0];
  const unitName =
    (typeof user?.organization === "object" &&
      (user?.organization?.name || user?.organization?.unit_name)) ||
    undefined;

  const slices: Slice[] = modules
    .map((key) => ({ key, label: moduleLabel(key), value: dataOf(key)?.total ?? 0, color: colorOf(key) }))
    .filter((s) => s.value > 0);

  const viewAll = (key: ModuleKey) => (
    <Link {...linkOf(key)} className="text-sm font-medium text-[#AA232F] hover:underline">
      {t("rd_view_all")}
    </Link>
  );

  const countOf = (key: ModuleKey) => {
    const result = results[key];
    if (!result || result.state === "loading") {
      return <span className="inline-block h-7 w-12 animate-pulse rounded bg-gray-100" />;
    }
    if (result.state === "error") {
      return (
        <span className="text-base font-medium text-gray-500" title={t("rd_load_failed")}>
          —
        </span>
      );
    }
    return <CountUp value={result.data.total} />;
  };

  const recentList = (key: ModuleKey | undefined) => {
    if (!key) return null;
    const data = dataOf(key);
    return (
      <Card key={key} title={`${t("rd_recent")}: ${moduleLabel(key)}`} action={viewAll(key)}>
        {data?.recent.length ? (
          <ul className="divide-y divide-gray-100">
            {data.recent.map((row) => (
              <li key={`${row.id}-${row.date ?? ""}`}>
                <Link
                  to={row.route}
                  state={row.route === defOf(key)?.route ? defOf(key)?.state : undefined}
                  className="flex items-center gap-3 py-3 hover:bg-gray-50"
                >
                  <span className="rounded-lg bg-[#FDEBEC] p-2 text-[#AA232F]">
                    <FileStack size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {row.title || "—"}
                    </span>
                    {row.subtitle && (
                      <span className="block truncate text-xs text-gray-500">{row.subtitle}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-right">
                    {row.status && (
                      <span className="block rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                        {t(row.status, { defaultValue: row.status })}
                      </span>
                    )}
                    {row.date && (
                      <span className="mt-1 block text-xs text-gray-500">{formatDate(row.date)}</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500">
            {results[key]?.state === "loading" ? t("rd_loading") : t("rd_no_data")}
          </p>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("rd_dashboard")}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t("rd_welcome_back")},{" "}
            <span className="font-semibold text-[#0A1D39]">{displayName}</span>. {t("rd_overview")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {roleName && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FDEBEC] px-3 py-1 text-xs font-medium text-[#AA232F] ring-1 ring-[#AA232F]/20">
                <UserRound size={14} />
                {roleName}
              </span>
            )}
            {unitName && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
                <Landmark size={14} />
                {unitName}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto">
          <div
            className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm"
            title={t("rd_period_hint")}
          >
            <CalendarDays size={14} className="mx-1.5 shrink-0 text-[#AA232F]" />
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition ${
                  period === p.key ? "bg-[#AA232F] text-white" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t(p.labelKey)}
              </button>
            ))}
            <select
              value={period.startsWith("year:") ? period : ""}
              onChange={(e) => setPeriod(e.target.value || "all")}
              style={{ width: "auto" }}
              className={`ml-0.5 cursor-pointer rounded-md border-0 py-1 pl-2 pr-6 text-xs font-medium focus:outline-none focus:ring-0 ${
                period.startsWith("year:") ? "bg-[#AA232F] text-white" : "bg-transparent text-gray-600 hover:bg-gray-100"
              }`}
            >
              <option value="" className="bg-white text-gray-700">
                {t("rd_select_year")}
              </option>
              {years.map((y) => (
                <option key={y} value={`year:${y}`} className="bg-white text-gray-700">
                  {t("rd_year_label", { year: y })}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {t("Refresh")}
          </button>
        </div>
      </div>

      {!modules.length ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow">
          {t("rd_no_modules")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {topCards.map((key, i) => {
              const first = dataOf(key)?.byStatus[0];
              const highlighted = i === 0;
              return (
                <Link
                  key={key}
                  {...linkOf(key)}
                  className={`flex flex-col justify-between rounded-xl p-5 shadow transition ${
                    highlighted
                      ? "bg-[#AA232F] text-white hover:bg-[#9D0303]"
                      : "border border-gray-200 bg-white hover:shadow-md"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`shrink-0 rounded-full p-2.5 ${highlighted ? "bg-white/15" : ""}`}
                      style={
                        highlighted
                          ? undefined
                          : { backgroundColor: `${colorOf(key)}1a`, color: colorOf(key) }
                      }
                    >
                      <BookOpen size={20} />
                    </span>
                    <div className="min-w-0">
                      <p
                        className={`truncate text-sm ${highlighted ? "text-red-100" : "text-gray-600"}`}
                        title={moduleLabel(key)}
                      >
                        {moduleLabel(key)}
                      </p>
                      <p className={`mt-1 text-3xl font-bold ${highlighted ? "" : "text-gray-900"}`}>
                        {countOf(key)}
                      </p>
                      <p className={`text-xs ${highlighted ? "text-red-100" : "text-gray-500"}`}>
                        {t("rd_in_your_list")}
                      </p>
                    </div>
                  </div>
                  <p
                    className={`mt-4 truncate text-xs ${highlighted ? "text-red-100" : "text-gray-500"}`}
                    title={first ? `${statusLabel(first)}: ${first.count}` : undefined}
                  >
                    {first ? `${statusLabel(first)}: ${first.count}` : " "}
                  </p>
                </Link>
              );
            })}
          </div>

          {chips.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {chips.map((key) => {
                const first = dataOf(key)?.byStatus[0];
                return (
                  <Link
                    key={key}
                    {...linkOf(key)}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow transition hover:shadow-md"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: colorOf(key) }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-gray-900" title={moduleLabel(key)}>
                          {moduleLabel(key)}
                        </span>
                        {first && (
                          <span className="block truncate text-xs text-gray-500">
                            {statusLabel(first)}: {first.count}
                          </span>
                        )}
                      </span>
                    </span>
                    <span
                      className="shrink-0 rounded-lg px-3 py-1 text-sm font-semibold"
                      style={{ backgroundColor: `${colorOf(key)}1a`, color: colorOf(key) }}
                    >
                      {countOf(key)}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          <div className={isEvaluator ? "grid grid-cols-1 gap-4 xl:grid-cols-5" : ""}>
            <Card
              className={isEvaluator ? "xl:col-span-2" : ""}
              title={
                <span className="flex items-center gap-2">
                  <ClipboardCheck size={18} className="text-[#AA232F]" />
                  {t("rd_waiting_for_you")}
                  {waiting.length > 0 && (
                    <span className="rounded-full bg-[#AA232F] px-2 py-0.5 text-xs font-semibold text-white">
                      {waiting.reduce((sum, w) => sum + w.status.count, 0)}
                    </span>
                  )}
                </span>
              }
            >
              {waiting.length ? (
                <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${isEvaluator ? "" : "xl:grid-cols-3"}`}>
                  {waiting.map(({ key, status }) => (
                    <Link
                      key={`${key}-${status.key}`}
                      {...linkOf(key)}
                      className="flex items-center justify-between gap-3 rounded-lg border border-[#AA232F]/20 bg-[#FDEBEC] px-4 py-3 transition hover:border-[#AA232F]/50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-gray-900">
                          {moduleLabel(key)}
                        </span>
                        <span className="block truncate text-xs text-[#AA232F]">{statusLabel(status)}</span>
                      </span>
                      <span className="shrink-0 text-2xl font-bold text-[#AA232F]">
                        <CountUp value={status.count} />
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-gray-500">
                  {stillLoading ? t("rd_loading") : t("rd_nothing_waiting")}
                </p>
              )}
            </Card>

            {isEvaluator && <EvaluationSummary user={user} bearer={bearer} className="xl:col-span-3" />}
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card title={t("rd_workload_by_module")}>
              {slices.length ? (
                <Donut slices={slices} centerLabel={t("rd_items")} />
              ) : (
                <p className="py-8 text-center text-sm text-gray-500">{t("rd_no_data")}</p>
              )}
            </Card>
            {[statusFirst, statusSecond].map((key) =>
              key ? (
                <Card key={key} title={`${moduleLabel(key)} — ${t("rd_by_status")}`} action={viewAll(key)}>
                  <CountBars
                    rows={(dataOf(key)?.byStatus ?? []).map((s) => ({
                      key: s.key,
                      label: statusLabel(s),
                      count: s.count,
                    }))}
                    total={dataOf(key)?.total}
                    emptyText={t("rd_no_data")}
                  />
                </Card>
              ) : null,
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            {(withRecent.length ? withRecent : main ? [main] : []).slice(0, 3).map((key) => recentList(key))}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
